import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin, tap } from 'rxjs';
import { userRoles } from '../../../../environments/environment';
import { UniversalChartComponent } from '../../../shared/components/charts/report-events/report-events.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import { AddAudienceComponent } from '../../../shared/components/dialog/add-audience/add-audience.component';
import { AddEventManagerComponent } from '../../../shared/components/dialog/add-event-managers/add-event-manager.component';
import { AddEventTypeComponent } from '../../../shared/components/dialog/add-event-type/add-event-type.component';
import { AddEventComponent } from '../../../shared/components/dialog/add-event/add-event.component';
import { AddFocusAreaComponent } from '../../../shared/components/dialog/add-focus-area/add-focus-area.component';
import { AddRegionComponent } from '../../../shared/components/dialog/add-region/add-region.component';
import { AddSchoolComponent } from '../../../shared/components/dialog/add-school/add-school.component';
import { AddStudentComponent } from '../../../shared/components/dialog/add-student/add-student.component';
import { AddTeacherComponent } from '../../../shared/components/dialog/add-teacher/add-teacher.component';
import { CommonDialogComponent } from '../../../shared/components/dialog/commom/commom-dialog.component';
import { InputComponent } from '../../../shared/components/from-input/input.component';
import { MultiSelectComponent } from '../../../shared/components/multi-select/multi-select.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { PasswordComponent } from '../../../shared/profile/password-temp/password.component';
import { ProfileComponent } from '../../../shared/profile/profile-temp/profile.component';
import { HttpService } from '../../../shared/services/http.service';
import { UrlParserService } from '../../../shared/services/url-parser.service';

interface TeacherDialogData {
  initialData?: APIResponse;
  EditMode: boolean;
}

interface CSQTeamMember {
  id: string;
  memberId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
}

interface EventType {
  id: string;
  eventName: string;
  eventDate: string;
}

interface APIResponse {
  status: boolean;
  message: string;
  data: any[];
}

interface Manager {
  id: string;
  eventName: string;
  eventDate: string;
}

interface StudentAPIResponse {
  id: any;
  name: any;
  dateOfBirth: string;
  schoolName: string;
  studentId: string;
  firstName: string;
  lastName: string;
  schoolEmail: string;
  mobileNumber: string;
  personalEmail: string;
  gender: string;
  postcode: string;
  teacherName: string;
  schoolId: string;
  ethnicity: string;
  teacherId: string;
  assignedTeacherName: boolean;
}

interface StudentApiDataResponse {
  status: boolean;
  message: string;
  data: StudentAPIResponse[] | StudentAPIResponse;
}

interface TeacherAPIResponse {
  schoolId: string;
  schoolName: string;
  address: string;
  suburb: string;
  postcode: string;
  teacherId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  isActive: boolean | string;
  dateOfBirth: string;
  assignedTeacher: string;
  name: string;
  id: string;
}

interface TeacherApiDataResponse {
  status: boolean;
  message: string;
  data: TeacherAPIResponse | TeacherAPIResponse[];
}

interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
}

interface DashboardRangePayload {
  fromDate: string;
  toDate: string;
  eventTypeId: string[];
  regionId: string[];
}

interface EventAnalysisFilterFormValue {
  startDate: string;
  endDate: string;
  eventTypes: string[];
  regions: string[];
}

@Component({
  selector: 'app-super-admin',
  standalone: true,
  imports: [
    PasswordComponent,
    ProfileComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatIconModule,
    TableComponent,
    MatMenuModule,
    MatButtonModule,
    MatDialogModule,
    InputComponent,
    AddRegionComponent,
    AddFocusAreaComponent,
    AddAudienceComponent,
    AddEventManagerComponent,
    AddEventTypeComponent,
    // EventTypeDashboardChartComponent,
    UniversalChartComponent,
    MultiSelectComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './super-admin.component.html',
  styleUrls: ['./super-admin.component.scss'],
  providers: [{ provide: MAT_DATE_LOCALE, useValue: 'en-GB' }],
})
export class SuperAdminComponent implements OnDestroy {
  @ViewChild('statusButton') showstatus!: TemplateRef<any>;
  @ViewChild('addEventContent') addEventContent!: TemplateRef<any>;
  @ViewChild('addStudentContent') addStudentContent!: TemplateRef<any>;
  @ViewChild('addSchoolContent') addSchoolContent!: TemplateRef<any>;
  @ViewChild('addSchoolContent2') addSchoolContent2!: TemplateRef<any>;
  @ViewChild('addTeacherContent') addTeacherContent!: TemplateRef<any>;
  @ViewChild('addEventTypeContent') addEventTypeContent!: TemplateRef<any>;
  @ViewChild('addEventManager') addEventManagerContent!: TemplateRef<any>;
  @ViewChild('addEventAudienceType')
  addEventAudienceTypeContent!: TemplateRef<any>;
  @ViewChild('addEventFocusArea') addEventFocusAreaContent!: TemplateRef<any>;
  @ViewChild('addEventRegion') addEventRegionContent!: TemplateRef<any>;
  @ViewChild('addCSQContent') addCSQContent!: TemplateRef<any>;
  eventLoading = false;
  eventAnalysisLoading = false;
  loadingReports = false;
  loadingRegionReports = false;
  isEditPage = false;
  csqForm!: FormGroup;
  csqLoading = false;
  selectedDate: Date = new Date();
  startDate!: Date;
  endDate!: Date;
  displayDateRange: string | undefined;
  loading = false;
  showFilter = false;
  isProcessing = false;
  selectedSegment: 'upcoming' | 'recent' = 'upcoming';
  user = {
    name: '',
    role: '',
    avatar: '',
    email: '',
  };
  selectedWeek: string = 'current';
  currentWeek: string = '';

  showAllEvents: boolean = false;
  columns: string[] = ['eventName', 'eventType', 'date', 'eventLocation'];
  schlEngagementColumns: string[] = [
    'school name',
    'number of events participated',
  ];
  reColumns: string[] = ['name', 'createdAt', 'recordType'];
  data: any[] = [];
  dashboardEventData: any[] = [];
  reData: any[] = [];
  schoolEngagementListData: any[] = [];
  eventDates: Date[] = [];

  eventOptions: any[] = [];
  schoolOptions: any[] = [];
  teacherstOption: any[] = [];
  teacherSchoolOptions: any[] = [];
  eventManagers: any[] = [];
  audienceTypes: any[] = [];
  focusAreaOptions: any[] = [];
  regionOptions: any[] = [];
  genderOption: any[] = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Non-identifying', value: 'non-identifying' },
    { label: 'Prefer not to say', value: 'prefernottosay' },
  ];
  private statusDropdownClickListener: (() => void) | null = null;
  showStatusDropdown = false;
  regions: any[] = [{ label: 'All', value: 'All' }];
  eventTypes: any[] = [{ label: 'All', value: 'All' }];
  focusAreaChart: any;
  completedEventsYearly: any;
  analysisByYear: any;
  analysisByYear2: any;
  selectedRegion: string[] = ['All'];
  selectedEventType: string[] = ['All'];
  eventAnalysisFilterForm!: FormGroup;
  studentForm!: FormGroup;
  eventForm!: FormGroup;
  schoolForm!: FormGroup;
  teacherForm!: FormGroup;
  fileControl: any;
  uploadedFiles: File[] = [];
  imagePreviews: string[] = [];
  editingEventType: EventType | null = null;
  editingManager: Manager | null = null;
  editingSchool: any = null;
  editingStudentIndex: number | null = null;
  editingTeacherIndex: number | null = null;
  editingEventIndex: number | null = null;
  selectedSchoolID: any = null;
  selectedStudentId: string | null = null;
  selectedTeacherID: string | null = null;
  selectedEvent: any = null;
  cardCount: any = null;
  teacherLoading = false;

  userRoles = {
    addTeacher: false,
    addStudent: false,
    addSchool: false,
    addEvent: false,
    addCSQAdmin: false,
  };
  roleValue: any = null;
  rotatingImageMap: { [eventId: string]: string[] } = {};
  currentImageIndexMap: { [eventId: string]: number } = {};
  imageIntervals: { [eventId: string]: any } = {};
  private eventTypeDialogRef: MatDialogRef<CommonDialogComponent> | null = null;
  private addSchoolDialogRef!: MatDialogRef<CommonDialogComponent>;
  private scrollPosition: number = 0;

  customErrorMessages = {
    eventName: { required: 'Event name is required.' },
    eventDate: { required: 'Please enter a valid date.' },
    eventLocation: { required: 'Event location is required.' },
    eventType: { required: 'Please select an event type.' },
    eventDescription: { required: 'Please enter a description.' },
    eventTime: { required: 'Please enter a valid time.' },
    eventManager: { required: 'Event Manager is required.' },
    audienceTypes: { required: 'Audience type is required.' },
    focusArea: { required: 'Please select at least one Focus Area.' },
    region: { required: 'Region is required.' },
    firstName: { required: 'Please enter a first name.' },
    lastName: { required: 'Please enter a last name.' },
    personalEmail: {
      required: 'Enter a valid personal email.',
      email: 'Please enter a valid email',
    },
    schoolEmail: {
      required: 'Enter a valid school email.',
      email: 'Please enter a valid email',
    },
    dob: {
      required: 'Enter a valid date of birth.',
      pattern: 'Please enter a valid date',
    },
    postcode: {
      required: 'Please enter a valid Postcode.',
      pattern: 'Please enter numeric value.',
    },
    mobileNumber: {
      required: 'Please enter a phone number.',
      pattern: 'Please enter a valid phone number.',
    },
    schoolName: { required: 'Please select a school.' },
    gender: { required: 'Please select a gender.' },
    assignedTeacher: { required: 'Please assign a teacher.' },
    ethnicity: { required: 'Ethnicity is required.' },
    schoolForm_schoolName: { required: 'School name is required.' },
    schoolForm_address: { required: 'Address is required.' },
    schoolForm_suburb: { required: 'Suburb is required.' },
    schoolForm_postcode: { required: 'Postcode is required.' },
    schoolForm_state: { required: 'State is required.' },
    email: {
      required: 'Please enter a email address.',
      email: 'Please enter a valid email address.',
    },
  };

  constructor(
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private adapter: DateAdapter<Date>,
    private urlParser: UrlParserService,
  ) {
    this.adapter.setLocale('en-GB');
    const UserDetailsStr = localStorage.getItem('userDetails') || '';
    let name = '';
    let email = '';
    try {
      const UserDetails = UserDetailsStr ? JSON.parse(UserDetailsStr) : null;
      if (UserDetails) {
        name =
          (UserDetails.firstName || '') + ' ' + (UserDetails.lastName || '');
        email = UserDetails?.email;
      }
    } catch (e) {
      name = '';
    }
    this.user.name = name.trim();
  }

  ngOnInit(): void {
    this.isEditPage = this.router.url.includes('/edit');
    this.initForms();
    this.initCSQForm();
    this.setDefaultDateRange();
    this.initEventAnalysisFilterForm();
    this.loadInitialData();

    const user = localStorage.getItem('userRole');
    if (user && user in userRoles) {
      this.userRoles = userRoles[user as keyof typeof userRoles]?.home?.add;
      this.roleValue = user;
    }
  }

  private initCSQForm(): void {
    this.csqForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      mobileNumber: [
        '',
        [
          Validators.required,
          // Validators.pattern(/^(?:\+?61|0)[ ]?[23478](?:[ ]?\d){8}$/)
          Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/),
        ],
      ],
    });
  }

  ngOnDestroy(): void {
    Object.values(this.imageIntervals).forEach((interval) =>
      clearInterval(interval),
    );
    document.body.classList.remove('no-scroll');
    document.body.style.top = '';
  }

  private initForms(): void {
    this.initStudentForm();
    this.initEventForm();
    this.initSchoolForm();
    this.initTeacherForm();
  }

  private initEventAnalysisFilterForm(): void {
    const defaults = this.getDefaultEventAnalysisFilterFormValue();

    this.eventAnalysisFilterForm = this.fb.group({
      startDate: [defaults.startDate, Validators.required],
      endDate: [defaults.endDate, Validators.required],
      eventTypes: [defaults.eventTypes],
      regions: [defaults.regions],
    });

    this.updateEventAnalysisDateValidation();
  }

  private getDefaultEventAnalysisFilterFormValue(): EventAnalysisFilterFormValue {
    return {
      startDate: this.formatDateToYMD(this.startDate) ?? '',
      endDate: this.formatDateToYMD(this.endDate) ?? '',
      eventTypes: this.selectedEventType.length
        ? [...this.selectedEventType]
        : ['All'],
      regions: this.selectedRegion.length ? [...this.selectedRegion] : ['All'],
    };
  }

  private syncEventAnalysisFormWithAppliedFilters(): void {
    if (!this.eventAnalysisFilterForm) {
      return;
    }

    this.eventAnalysisFilterForm.patchValue(
      {
        startDate: this.formatDateToYMD(this.startDate) ?? '',
        endDate: this.formatDateToYMD(this.endDate) ?? '',
        eventTypes: [...this.selectedEventType],
        regions: [...this.selectedRegion],
      },
      { emitEvent: false },
    );

    this.updateEventAnalysisDateValidation();
  }

  private parseFilterDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const dateValue = String(value).split('T')[0];
    const parts = dateValue.split('-').map(Number);

    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }

    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  private updateEventAnalysisDateValidation(): void {
    if (!this.eventAnalysisFilterForm) {
      return;
    }

    const startControl = this.eventAnalysisFilterForm.get('startDate');
    const endControl = this.eventAnalysisFilterForm.get('endDate');

    if (!startControl || !endControl) {
      return;
    }

    const startDate = this.parseFilterDate(startControl.value);
    const endDate = this.parseFilterDate(endControl.value);
    const errors = { ...(endControl.errors || {}) };

    delete errors['dateRange'];

    if (startDate && endDate && endDate <= startDate) {
      errors['dateRange'] = true;
    }

    endControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
  }

  private markEventAnalysisFilterFormTouched(): void {
    if (!this.eventAnalysisFilterForm) {
      return;
    }

    Object.values(this.eventAnalysisFilterForm.controls).forEach((control) => {
      control.markAsTouched();
      control.markAsDirty();
      control.updateValueAndValidity({ onlySelf: true });
    });
  }

  private syncAppliedFiltersFromForm(): boolean {
    if (!this.eventAnalysisFilterForm) {
      return false;
    }

    this.updateEventAnalysisDateValidation();

    const startDate = this.parseFilterDate(
      this.eventAnalysisFilterForm.get('startDate')?.value,
    );
    const endDate = this.parseFilterDate(
      this.eventAnalysisFilterForm.get('endDate')?.value,
    );

    if (!startDate || !endDate || this.eventAnalysisFilterForm.invalid) {
      this.markEventAnalysisFilterFormTouched();
      return false;
    }

    this.startDate = startDate;
    this.endDate = endDate;
    this.selectedEventType = [
      ...((this.eventAnalysisFilterForm.get('eventTypes')?.value as string[]) ||
        []),
    ];
    this.selectedRegion = [
      ...((this.eventAnalysisFilterForm.get('regions')?.value as string[]) ||
        []),
    ];

    return true;
  }

  private loadInitialData(): void {
    // this.fetchEvents();
    this.getCurrentWeek();
    this.onEventDateClick(new Date());
    this.loadUserData();
    this.loadDashboardReports();
    this.fetchSchoolEngagementData();
    this.loadEventAnalysis();
    this.loadCompletedEventChart();
    this.loadDropdownData();
    this.fetchSchoolsEngagement();
  }

  initStudentForm(): void {
    this.studentForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      personalEmail: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      schoolEmail: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      schoolName: ['', Validators.required],
      dob: ['', Validators.required],
      mobileNumber: [
        '',
        [
          Validators.required,
          // Validators.pattern(/^(?:\+?61|0)[ ]?[23478](?:[ ]?\d){8}$/)
          Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/),
        ],
      ],
      postcode: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      gender: ['', Validators.required],
      assignedTeacher: ['', Validators.required],
      ethnicity: ['', Validators.required],
    });
  }

  initTeacherForm(): void {
    this.teacherForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      phoneNumber: [
        '',
        [
          Validators.required,
          // Validators.pattern(/^(?:\+?61|0)[ ]?[23478](?:[ ]?\d){8}$/)
          Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/),
        ],
      ],
      school: ['', Validators.required],
    });
  }

  initEventForm(): void {
    this.eventForm = this.fb.group({
      eventName: ['', Validators.required],
      eventDate: ['', Validators.required],
      eventLocation: ['', Validators.required],
      eventType: ['', Validators.required],
      eventDescription: ['', Validators.required],
      eventManager: ['', Validators.required],
      audienceTypes: ['', Validators.required],
      eventTime: ['', Validators.required],
      imageUpload: [null, Validators.required],
      focusArea: [[], Validators.required],
      region: ['', Validators.required],
    });
    this.fileControl = this.eventForm.get('imageUpload') as FormControl;
  }

  initSchoolForm(): void {
    this.schoolForm = this.fb.group({
      schoolName: ['', Validators.required],
      address: ['', Validators.required],
      suburb: ['', Validators.required],
      postcode: ['', Validators.required],
      state: ['', Validators.required],
    });
  }

  fetchEvents(): void {
    this.loading = true;
    this.httpService.get<APIResponse>('events').subscribe({
      next: (response) => {
        this.data = response?.data;
      },
      error: (err) => {
        console.error('Failed to load events:', err);
        // this.showErrorToast('Failed to load events');
      },
      complete: () => (this.loading = false),
    });
  }

  formatWeekLabel(start: string, end: string): string {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const weekStart = this.getWeekStart(startDate);
    const weekEnd = this.getWeekEnd(weekStart);

    const fridayDate = new Date(weekEnd);
    const dayOfWeek = fridayDate.getDay();
    if (dayOfWeek === 0) {
      fridayDate.setDate(fridayDate.getDate() - 2);
    } else if (dayOfWeek === 6) {
      fridayDate.setDate(fridayDate.getDate() - 1);
    }

    const startMonth = weekStart.toLocaleString('en-US', { month: 'short' });
    const endMonth = fridayDate.toLocaleString('en-US', { month: 'short' });

    const startDay = weekStart.getDate();
    const endDay = fridayDate.getDate();

    if (startMonth === endMonth) {
      return `Week of ${startMonth} ${startDay} - ${endDay}`;
    }

    return `Week of ${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  }

  fetchDashBoardEvents(
    start?: Date,
    end?: Date,
    weekType: number | null = null,
  ): void {
    let url = 'dashboard/upcoming-events';
    this.eventLoading = true;
    if (start && end) {
      const fromDate = this.formatDate(start);
      const toDate = this.formatDate(end);
      this.currentWeek = this.formatWeekLabel(fromDate, toDate);
      url += `?weekStart=${fromDate}&weekEnd=${toDate}`;
      url += `&weekType=${weekType ?? ''}`;
    }

    this.httpService.get<APIResponse>(url).subscribe({
      next: (response) => {
        this.dashboardEventData = Array.isArray(response?.data)
          ? response.data.map((item) => this.mapEventData(item))
          : [];
        this.eventLoading = false;
      },
      error: (err) => {
        console.error('Failed to load events:', err);
        this.eventLoading = false;
      },
      complete: () => (this.eventLoading = false),
    });
  }

  navigateToEvents(event: any) {
    const eventId = event?.eventId || event?.id;
    if (!eventId) {
      return;
    }

    const encodedId = this.urlParser.encodeId(String(eventId));
    const encodedSegment = this.urlParser.encodeId(event?.segment || 'admin');

    this.router.navigate(['/events/event-list/view', encodedId], {
      queryParams: { segment: encodedSegment },
    });
  }

  private mapEventData(item: any): any {
    return {
      id: item.eventId,
      eventId: item.eventId,
      eventName: item.eventName || '-',
      eventType: item.eventTypeName || item.eventType || null,
      eventLocation: item.location || null,
      imagePath: item.imagePath || null,
      eventStatus: item.eventStatus || null,
      eventTime: item.eventTime || null,
      eventStartDate: item.eventStartDate || null,
      eventHostRegionName: item.eventHostRegionName || '-',
      createdBy: item.createdBy || '',
      segment: this.resolveUpcomingEventSegment(item.createdBy),
      imageUrl: this.resolveUpcomingEventImage(
        item.imagePath || item.eventTypeImage,
      ),
    };
  }

  private resolveUpcomingEventImage(imageValue: unknown): string {
    if (Array.isArray(imageValue) && imageValue.length > 0) {
      return imageValue[0];
    }

    if (typeof imageValue === 'string' && imageValue.trim()) {
      try {
        const parsed = JSON.parse(imageValue);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]) {
          return parsed[0];
        }
      } catch {
        return imageValue;
      }

      return imageValue;
    }

    return 'assets/images/placeholder-image.svg';
  }

  private resolveUpcomingEventSegment(
    createdBy: string | null | undefined,
  ): string {
    const role = String(createdBy || '')
      .trim()
      .toLowerCase();

    if (role.includes('student')) {
      return 'student';
    }

    if (role.includes('teacher')) {
      return 'teacher';
    }

    return 'admin';
  }

  private parseCustomDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  fetchRecentDetails(): void {
    this.loading = true;
    this.httpService
      .get<APIResponse>('dashboard/recent-additions-unified')
      .subscribe({
        next: (response) => {
          this.reData = Array.isArray(response?.data)
            ? response.data.map((data) => ({
                id: data.recordId ?? '-',
                name: data.name ?? '-',
                createdAt: data.createdAt
                  ? new Date(data.createdAt).toLocaleDateString('en-GB')
                  : '-',
                recordType: data.recordType ?? '-',
              }))
            : [];
        },
        error: (err) => {
          console.error('Failed to load recent details:', err);
          // this.showErrorToast('Failed to load recent details');
        },
        complete: () => (this.loading = false),
      });
  }

  fetchEventsDropDown() {
    return this.httpService.get<APIResponse>('eventTypes').pipe(
      tap({
        next: (response) => {
          this.eventOptions = Array.isArray(response.data)
            ? response.data
                .filter((item) => item.status === 'Active')
                .map((item) => ({
                  label: item.eventTypeName || item.name,
                  value: item.eventTypeId,
                }))
            : [];
        },
        error: (err) => {
          this.eventOptions = [];
          this.showErrorToast(err?.message);
        },
      }),
      finalize(() => (this.loading = false)),
    );
  }

  fetchSchoolsDropDown() {
    return this.httpService
      .get<StudentApiDataResponse>('Schools/dropdown')
      .pipe(
        tap({
          next: (response) => {
            this.schoolOptions = Array.isArray(response.data)
              ? response.data.map((item) => ({
                  label: item.name || item.schoolName,
                  value: item.id || item.schoolId,
                }))
              : [];
          },
          error: () => {
            this.schoolOptions = [];
            // this.showErrorToast('Failed to load schools');
          },
        }),
      );
  }

  fetchTeachersDropDown() {
    return this.httpService
      .get<StudentApiDataResponse>('Teachers/dropdown')
      .pipe(
        tap({
          next: (response) => {
            this.teacherstOption = Array.isArray(response.data)
              ? response.data.map((item) => ({
                  label: item.name,
                  value: item.id || item.teacherId,
                }))
              : [];
          },
          error: () => {
            this.teacherstOption = [];
            // this.showErrorToast('Failed to load teachers');
          },
        }),
      );
  }

  formatSideEventTime(time: string): string | null {
    if (!time) return '';
    return time.split('t')[0];
  }

  formatDateToYMD(date: Date | null): string | null {
    if (!date) return null;
    if (isNaN(date.getTime())) {
      console.error('Invalid date provided:', date);
      return null;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private buildDashboardRangePayload(): DashboardRangePayload | null {
    const fromDate = this.formatDateToYMD(this.startDate);
    const toDate = this.formatDateToYMD(this.endDate);

    if (!fromDate || !toDate) {
      return null;
    }

    const hasAllEventType = this.selectedEventType.includes('All');
    const hasAllRegion = this.selectedRegion.includes('All');

    return {
      fromDate: `${fromDate}T00:00:00`,
      toDate: `${toDate}T00:00:00`,
      eventTypeId: hasAllEventType ? [] : [...this.selectedEventType],
      regionId: hasAllRegion ? [] : [...this.selectedRegion],
    };
  }

  loadDashboardReports(): void {
    if (!this.startDate || !this.endDate) {
      console.warn('Please select date range');
      return;
    }

    this.loadingReports = true;

    const fromDate = this.formatDateToYMD(this.startDate);
    const toDate = this.formatDateToYMD(this.endDate);

    if (!fromDate || !toDate) {
      this.loadingReports = false;
      return;
    }

    this.httpService
      .get(`dashboard/focus-area-graph?fromDate=${fromDate}&toDate=${toDate}`)
      .subscribe({
        next: (res: any) => {
          if (res?.data?.data) {
            this.buildFocusAreaChart(res.data.data);
          } else {
            console.warn('No data in focus area response');
            this.buildFocusAreaChart([]);
          }
          this.loadingReports = false;
        },
        error: (error) => {
          console.error('Failed to load dashboard reports:', error);
          this.loadingReports = false;
          this.showErrorToast('Failed to load dashboard reports');
          this.buildFocusAreaChart([]);
        },
      });
  }

  loadCompletedEventChart(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }

    this.loadingRegionReports = true;
    const payload = this.buildDashboardRangePayload();

    if (!payload) {
      this.loadingRegionReports = false;
      return;
    }

    this.httpService
      .post<ApiResponse<any>>('dashboard/engaged-school-range', payload)
      .subscribe({
        next: (res: any) => {
          this.loadingRegionReports = false;
          if (res?.status && res?.data) {
            this.buildSchoolEngagementChart(res.data);
          } else {
            this.buildSchoolEngagementChart([]);
          }
        },
        error: (error) => {
          console.error('Failed to load school engagement data:', error);
          this.showErrorToast('Failed to load school engagement data');
          this.loadingRegionReports = false;
          this.buildSchoolEngagementChart([]);
        },
      });
  }

  refreshAllCharts(): void {
    if (!this.startDate || !this.endDate) {
      console.warn('Cannot refresh charts: dates not set');
      return;
    }

    this.loadEventAnalysis();
    this.fetchSchoolsEngagement();

    setTimeout(() => {
      this.loadDashboardReports();
    }, 100);

    setTimeout(() => {
      this.loadCompletedEventChart();
    }, 200);
  }

  onDateRangeSelected(): void {
    this.refreshAllCharts();
  }

  buildFocusAreaChart(apiData: any[]) {
    const categories = apiData.map((item) => item.eventTypeName);

    const focusAreaSet = new Set<string>();
    apiData.forEach((item) => {
      item.focusAreas.forEach((fa: any) => {
        focusAreaSet.add(fa.focusAreaName);
      });
    });

    const focusAreas = Array.from(focusAreaSet);

    const series = focusAreas.map((focusArea) => ({
      name: focusArea,
      data: apiData.map((eventType) => {
        const found = eventType.focusAreas.find(
          (fa: any) => fa.focusAreaName === focusArea,
        );
        return found ? found.totalEvents : 0;
      }),
    }));

    const totals = categories.map((_, index) => {
      return series.reduce((sum, s) => sum + (s.data[index] || 0), 0);
    });

    const maxTotal = Math.max(...totals);
    const yAxisMax = Math.ceil(maxTotal * 1.15);

    this.focusAreaChart = {
      series,
      chart: {
        type: 'bar',
        height: 350,
        background: '#404040',
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        spacing: 10,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '45%',
          dataLabels: {
            position: 'top',
            total: {
              enabled: true,
              style: {
                fontSize: '12px',
                fontWeight: 600,
                color: '#fff',
              },
              offsetY: -5,
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories,
        labels: {
          style: { colors: '#fff', fontSize: '10px', fontWeight: 200 },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        forceNiceScale: true,
        decimalsInFloat: 0,
        labels: {
          style: { colors: '#888', fontSize: '12px' },
          formatter: (value: number) => {
            return Math.round(value).toString();
          },
        },
      },

      colors: ['#2CC3E8'],
      legend: {
        show: false,
        position: 'bottom',
        labels: { colors: '#fff' },
      },
    };
  }

  buildRegionDonutChart(res: any) {
    const yearWiseEvents = res.yearWiseEvents || [];
    const series = yearWiseEvents.map((item: any) => item.total);
    const labels = yearWiseEvents.map((item: any) => item.year.toString());

    this.completedEventsYearly = {
      series,
      chart: {
        type: 'donut',
        width: '100%',
        height: 350,
        spacing: 10,
        background: '#404040',
      },
      labels,
      plotOptions: {
        pie: {
          donut: {
            size: '80%',
          },
        },
      },

      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
      },
    };
  }

  buildMonthEventChart(data: any[]): void {
    const categories = data.map((item) => item.eventTypeName);

    const upcomingSeries = data.map((item) =>
      item.upcoming > 0 ? item.upcoming : null,
    );
    const completedSeries = data.map((item) =>
      item.completed > 0 ? item.completed : null,
    );

    const maxValue = Math.max(
      ...data.map((item) => Math.max(item.upcoming ?? 0, item.completed ?? 0)),
    );

    const yAxisMax = Math.max(maxValue + 5, 10);

    const minWidthPerGroup = 100;
    const overflowThreshold = 6;
    const chartWidth =
      categories.length > overflowThreshold
        ? categories.length * minWidthPerGroup
        : '100%';

    const getTickAmount = (max: number) => {
      if (max <= 10) return 3;
      if (max <= 20) return 4;
      if (max <= 50) return 5;
      return 6;
    };

    this.analysisByYear = {
      series: [
        { name: 'Upcoming', data: upcomingSeries },
        { name: 'Completed', data: completedSeries },
      ],

      chart: {
        type: 'bar',
        height: 340,
        width: chartWidth,
        stacked: false,
        background: 'transparent',
        toolbar: { show: false },
        animations: { enabled: true, speed: 400 },
        fontFamily: 'inherit',
      },

      colors: ['#2CC3E8', '#50B748'],

      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '35px',
          borderRadius: 0,
          borderRadiusApplication: 'end',
          dataLabels: { position: 'top' },
          grouping: true,
        },
      },

      stroke: {
        show: true,
        width: 3,
        colors: ['transparent'],
      },

      dataLabels: {
        enabled: true,
        offsetY: -18,
        style: {
          fontSize: '11px',
          fontWeight: '600',
          colors: ['#e2e8f0'],
        },
        formatter: (val: number) =>
          val != null && val > 0 ? val.toString() : '',
      },

      legend: {
        show: true,
        position: 'bottom',
        fontSize: '10px',
        labels: { colors: '#fff' },
        markers: {
          width: 10,
          height: 10,
          radius: 50,
          shape: 'circle',
          strokeWidth: 0,
        },
      },

      tooltip: {
        enabled: true,
        theme: 'dark',
        shared: true,
        intersect: false,
        y: {
          formatter: (val: number) => (val != null ? val.toString() : '0'),
        },
      },

      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: Array(categories.length).fill('#94a3b8'),
            fontSize: '11px',
            fontWeight: 500,
          },
          rotate: -45,
          hideOverlappingLabels: false,
          trim: false,
          maxHeight: 120,
        },
      },

      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount: getTickAmount(yAxisMax),
        forceNiceScale: true,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: { colors: '#64748b', fontSize: '11px' },
          formatter: (value: number) => Math.round(value).toString(),
        },
      },

      title: {
        text: 'Top Engaged Schools',
        align: 'left',
        offsetX: 4,
        style: {
          color: '#f1f5f9',
          fontSize: '15px',
          fontWeight: '600',
        },
      },

      grid: {
        borderColor: '#334155',
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { left: 4, right: 4 },
      },
    };
  }

  buildSchoolEngagementChart(apiData: any[]): void {
    if (!apiData || apiData.length === 0) {
      this.analysisByYear2 = this.getEmptySchoolChart();
      return;
    }

    const schoolDataMap = new Map<
      string,
      { upcoming: number; completed: number; schoolName: string }
    >();

    apiData.forEach((yearData) => {
      if (yearData.series && Array.isArray(yearData.series)) {
        yearData.series.forEach((monthData: any) => {
          if (monthData.schools && Array.isArray(monthData.schools)) {
            monthData.schools.forEach((school: any) => {
              const schoolName = school.schoolName;
              if (schoolName && schoolName.trim() !== '') {
                if (!schoolDataMap.has(schoolName)) {
                  schoolDataMap.set(schoolName, {
                    schoolName: schoolName,
                    upcoming: 0,
                    completed: 0,
                  });
                }
                const current = schoolDataMap.get(schoolName)!;
                current.upcoming += school.upcoming || 0;
                current.completed += school.completed || 0;
              }
            });
          }
        });
      }
    });

    let schoolData = Array.from(schoolDataMap.values())
      .map((school) => ({
        ...school,
        total: school.upcoming + school.completed,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);

    if (schoolData.length === 0) {
      this.analysisByYear2 = this.getEmptySchoolChart();
      return;
    }

    const categories = schoolData.map((school) => school.schoolName);
    const upcomingData = schoolData.map((school) => school.upcoming);
    const completedData = schoolData.map((school) => school.completed);

    const maxValue = Math.max(...upcomingData, ...completedData);
    let yAxisMax = this.getDynamicMaxForSchoolChart(maxValue);

    this.analysisByYear2 = {
      series: [
        {
          name: 'Upcoming',
          data: upcomingData,
        },
        {
          name: 'Completed',
          data: completedData,
        },
      ],
      chart: {
        type: 'bar',
        height: 350,
        spacing: 10,
        stacked: true,
        background: '#404040',
        toolbar: { show: false },
      },
      colors: ['#2CC3E8', '#50B748'],
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 0,
          dataLabels: {
            position: 'top',
            total: {
              enabled: true,
              offsetY: -8,
              style: {
                fontSize: '11px',
                fontWeight: 'bold',
                color: '#ffffff',
              },
              formatter: (val: number) => (val > 0 ? val.toString() : ''),
            },
          },
        },
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: '#fff',
            fontSize: '11px',
            fontWeight: 400,
          },
          rotate: -45,
          hideOverlappingLabels: true,
          trim: false,
        },
      },
      yaxis: {
        min: 0,
        max: yAxisMax + Math.ceil(yAxisMax * 0.2),
        tickAmount: yAxisMax <= 20 ? 5 : 6,
        labels: {
          style: {
            colors: '#888',
            fontSize: '12px',
          },
          formatter: (value: number) => {
            return Math.round(value).toString();
          },
        },
      },
      title: {
        text: 'Top Engaged Schools',
        align: 'center',
        style: {
          color: '#fff',
          fontSize: '14px',
          fontWeight: 500,
        },
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} events`,
        },
      },
      grid: {
        borderColor: '#666',
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
    };
  }

  fetchSchoolEngagementData(): void {
    if (!this.startDate || !this.endDate) {
      console.warn('Dates not set for school engagement');
      return;
    }

    this.loadingRegionReports = true;
    const payload = this.buildDashboardRangePayload();

    if (!payload) {
      this.loadingRegionReports = false;
      return;
    }

    this.httpService
      .post<ApiResponse<any>>('dashboard/engaged-school-range', payload)
      .subscribe({
        next: (res: any) => {
          this.loadingRegionReports = false;
          if (res?.status && res?.data) {
            this.buildSchoolEngagementChart(res.data);
          } else {
            console.warn('No data in school engagement response');
            this.buildSchoolEngagementChart([]);
          }
        },
        error: (error) => {
          console.error('Failed to load school engagement data:', error);
          this.showErrorToast('Failed to load school engagement data');
          this.loadingRegionReports = false;
          this.buildSchoolEngagementChart([]);
        },
      });
  }

  getEmptySchoolChart(): any {
    return {
      series: [
        { name: 'Upcoming', data: [] },
        { name: 'Completed', data: [] },
      ],
      chart: {
        type: 'bar',
        height: 350,
        background: '#404040',
        toolbar: { show: false },
      },
      xaxis: {
        categories: [],
        labels: { style: { colors: '#fff' } },
      },
      yaxis: {
        min: 0,
        max: 10,
        labels: { style: { colors: '#888' } },
      },
      title: {
        text: 'No School Data Available',
        align: 'center',
        style: { color: '#fff' },
      },
      noData: {
        text: 'No data available for selected period',
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#fff', fontSize: '14px' },
      },
    };
  }

  getDynamicMaxForSchoolChart(maxValue: number): number {
    if (maxValue <= 5) return 5;
    if (maxValue <= 10) return 10;
    if (maxValue <= 20) return 20;
    if (maxValue <= 50) return 50;
    if (maxValue <= 100) return 100;
    return Math.ceil(maxValue / 50) * 50;
  }

  getDynamicMax(values: number[]): number {
    const max = Math.max(...values, 0);

    if (max <= 5) return 5;
    if (max <= 10) return 10;
    if (max <= 20) return 20;
    if (max <= 50) return 50;

    return Math.ceil(max / 10) * 10;
  }

  calculateYAxisMax(series: any[]): number {
    if (!series?.length) return 0;

    const dataLength = series[0].data.length;
    const totals: number[] = new Array(dataLength).fill(0);

    series.forEach((s) => {
      s.data.forEach((value: number, index: number) => {
        totals[index] += value || 0;
      });
    });

    const max = Math.max(...totals);

    return Math.ceil(max * 1.2);
  }

  getPreviousWeek() {
    const today = new Date();
    const currentStart = this.getWeekStart(today);

    const prevWeekDate = new Date(currentStart);
    prevWeekDate.setDate(prevWeekDate.getDate() - 7);
    const prevStart = this.getWeekStart(prevWeekDate);
    const prevEnd = this.getWeekEnd(prevStart);
    this.fetchDashBoardEvents(prevStart, prevEnd, 1);
  }

  getCurrentWeek() {
    const today = new Date();
    const currentStart = this.getWeekStart(today);
    const currentEnd = this.getWeekEnd(currentStart);
    this.fetchDashBoardEvents(currentStart, currentEnd);
  }

  getNextWeek() {
    const today = new Date();
    const currentStart = this.getWeekStart(today);

    const nextWeekDate = new Date(currentStart);
    nextWeekDate.setDate(nextWeekDate.getDate() + 7);
    const nextStart = this.getWeekStart(nextWeekDate);
    const nextEnd = this.getWeekEnd(nextStart);
    this.fetchDashBoardEvents(nextStart, nextEnd);
  }

  private getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  private getWeekEnd(startDate: Date): Date {
    const end = new Date(startDate);
    end.setDate(end.getDate() + 6);
    return end;
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  onWeekselection(week: string) {
    this.eventLoading = true;
    this.dashboardEventData = [];
    this.selectedWeek = week;
    if (week == 'previous') {
      this.getPreviousWeek();
    } else if (week == 'current') {
      this.getCurrentWeek();
    } else if (week == 'next') {
      this.getNextWeek();
    }
  }

  navigateToEventsPage(): void {
    this.router.navigate(['/events/event-list']);
  }

  formatDateItem(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  loadEventAnalysis(): void {
    if (!this.startDate || !this.endDate) {
      console.warn('Dates not set for event analysis');
      return;
    }
    this.eventAnalysisLoading = true;
    const payload = this.buildDashboardRangePayload();

    if (!payload) {
      this.eventAnalysisLoading = false;
      return;
    }

    this.httpService
      .post<ApiResponse<any>>('dashboard/event-analysis-range', payload)
      .subscribe({
        next: (res: any) => {
          this.eventAnalysisLoading = false;

          if (res?.status) {
            let data = res?.data;
            const transformedData = this.transformEventAnalysisData(data);
            this.buildMonthEventChart(transformedData);
          } else {
            console.warn('No series data in response:', res);
            this.buildMonthEventChart([]);
          }
        },
        error: (error) => {
          console.error('Failed to load event analysis:', error);
          this.showErrorToast('Failed to load event analysis');
          this.eventAnalysisLoading = false;
          this.buildMonthEventChart([]);
        },
      });
  }

  fetchSchoolsEngagement(): void {
    if (!this.startDate || !this.endDate) {
      console.warn('Dates not set for event analysis');
      return;
    }
    this.eventAnalysisLoading = true;
    const payload = this.buildDashboardRangePayload();

    if (!payload) {
      this.eventAnalysisLoading = false;
      return;
    }

    this.httpService
      .post<ApiResponse<any>>('dashboard/event-analysis-school-list', payload)
      .subscribe({
        next: (res: any) => {
          this.eventAnalysisLoading = false;

          if (res?.status) {
            const rawData = res?.data || [];

            this.schoolEngagementListData = rawData.map((item: any) => ({
              'school name': item?.schoolName || '',
              'number of events participated':
                item?.totalEventsParticipated || 0,
            }));
          }
        },
        error: (error) => {
          console.error('Failed to load event analysis:', error);
          this.showErrorToast('Failed to load event analysis');
          this.eventAnalysisLoading = false;
        },
      });
  }

  transformEventAnalysisData(apiData: any[]): any[] {
    const eventTypeMap = new Map<
      string,
      { upcoming: number; completed: number }
    >();

    apiData.forEach((yearItem) => {
      yearItem.series.forEach((monthItem: any) => {
        if (monthItem.eventTypes && Array.isArray(monthItem.eventTypes)) {
          monthItem.eventTypes.forEach((eventType: any) => {
            const eventTypeName = eventType.eventTypeName;

            if (!eventTypeMap.has(eventTypeName)) {
              eventTypeMap.set(eventTypeName, { upcoming: 0, completed: 0 });
            }

            const current = eventTypeMap.get(eventTypeName)!;
            current.upcoming += eventType.upcoming || 0;
            current.completed += eventType.completed || 0;
          });
        }
      });
    });
    const chartData = Array.from(eventTypeMap.entries()).map(
      ([eventTypeName, values]) => ({
        eventTypeName,
        upcoming: values.upcoming,
        completed: values.completed,
      }),
    );
    chartData.sort((a, b) => a.eventTypeName.localeCompare(b.eventTypeName));

    return chartData;
  }

  loadUserData(): void {
    this.loading = true;
    this.httpService.get('dashboard/user-data').subscribe({
      next: (res: any) => {
        this.cardCount = res.data;
        this.loading = false;
      },
      error: () => {
        this.showErrorToast('Failed to load user data');
        this.loading = false;
      },
    });
  }

  mapToOptions(data: any[], labelKey: string, valueKey: string) {
    return (data || []).map((item) => ({
      label: item[labelKey],
      value: item[valueKey],
    }));
  }

  loadDropdownData(): void {
    forkJoin({
      types: this.getEventTypes(),
      regions: this.getEventHostRegions(),
    }).subscribe({
      next: ({ types, regions }) => {
        this.eventTypes = [
          { label: 'All', value: 'All' },
          ...(this.mapToOptions(types?.data, 'eventTypeName', 'eventTypeId') ??
            []),
        ];
        this.regions = [
          { label: 'All', value: 'All' },
          ...(this.mapToOptions(
            regions?.data,
            'eventHostRegionName',
            'eventHostRegionId',
          ) ?? []),
        ];
      },
      error: () => this.showErrorToast('Failed to load report filters'),
    });
  }

  getEventTypes() {
    return this.httpService.get<ApiResponse<any[]>>('eventtypes?status=1');
  }

  getEventHostRegions() {
    return this.httpService.get<ApiResponse<any[]>>(
      'eventhostregions?status=1',
    );
  }

  fetchAudienceType(): void {
    this.loading = true;
    this.httpService.get<APIResponse>('audiencetypes').subscribe({
      next: (response) => {
        this.audienceTypes = Array.isArray(response.data)
          ? response.data.map((item) => ({
              label: item.audienceTypeName,
              value: item.audienceTypeId,
            }))
          : [];
      },
      error: (err) => {
        console.error('Failed to load audience types:', err);
      },
      complete: () => (this.loading = false),
    });
  }

  fetchFocusArea(): void {
    this.loading = true;
    this.httpService.get<APIResponse>('focusareas').subscribe({
      next: (response) => {
        this.focusAreaOptions = Array.isArray(response.data)
          ? response.data.map((item) => ({
              label: item.focusAreaName,
              value: item.focusAreaId,
            }))
          : [];
      },
      error: (err) => {
        console.error('Failed to load focus areas:', err);
        // this.showErrorToast('Failed to load focus areas');
      },
      complete: () => (this.loading = false),
    });
  }

  fetchRegions(): void {
    this.loading = true;
    this.httpService.get<APIResponse>('eventhostregions').subscribe({
      next: (response) => {
        this.regionOptions = Array.isArray(response.data)
          ? response.data.map((item) => ({
              label: item.eventHostRegionName,
              value: item.eventHostRegionId,
            }))
          : [];
      },
      error: (err) => {
        console.error('Failed to load regions:', err);
        // this.showErrorToast('Failed to load regions');
      },
      complete: () => (this.loading = false),
    });
  }

  openAddStudentDialog(
    teacherToEdit: APIResponse | undefined = undefined,
    isEdit: boolean = false,
    width: string = '50.5rem',
  ) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: teacherToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    const dialogRef = this.dialog.open(AddStudentComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // this.fetchStudents();
      }
      this.isProcessing = false;
    });
  }

  openAddTeacherDialog(
    teacherToEdit: APIResponse | undefined = undefined,
    isEdit: boolean = false,
    width: string = '50.5rem',
  ) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.editingTeacherIndex = null;

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: teacherToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    const dialogRef = this.dialog.open(AddTeacherComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // this.fetchTeachers();
      }
      this.isProcessing = false;
    });
  }

  private resetEventForm(): void {
    this.editingEventIndex = null;
    this.eventForm.reset();
    this.uploadedFiles = [];
    this.imagePreviews = [];
    this.fileControl = this.eventForm.get('imageUpload') as FormControl;
  }

  private loadEventDropdowns(): void {
    this.fetchAudienceType();
    this.fetchFocusArea();
    this.fetchRegions();
  }

  private patchEventForm(event: any): void {
    this.editingEventIndex = this.data.findIndex((s) => s.id === event.id);

    const selectedEventType = this.eventOptions.find(
      (opt) => opt.value === event.eventTypeId,
    );
    const selectedFocusArea = this.focusAreaOptions.find(
      (opt) => opt.value === event.focusArea,
    );
    const selectedManager = this.eventManagers.find(
      (opt) => opt.value === event.eventManager,
    );
    const selectedAudienceType = this.audienceTypes.find(
      (opt) => opt.value === event.audienceType,
    );
    const selectedRegion = this.regionOptions.find(
      (opt) => opt.value === event.eventHostRegion,
    );

    if (event.imagePath) {
      try {
        this.imagePreviews = JSON.parse(event.imagePath);
        this.fileControl.setValue(
          this.imagePreviews.length > 0 ? 'uploaded' : null,
        );
      } catch (e) {
        this.imagePreviews = [];
      }
    }

    this.eventForm.patchValue({
      eventName: event.eventName || '',
      eventDate: event.eventDate || '',
      eventLocation: event.location || '',
      eventType: selectedEventType?.value || '',
      eventDescription: event.description || '',
      eventManager: selectedManager?.value || '',
      eventTime: this.parseEventTimeString(event.eventTime || ''),
      audienceTypes: selectedAudienceType?.value || '',
      focusArea: selectedFocusArea?.value || '',
      region: selectedRegion?.value || '',
    });
  }

  openAddEventTypeDialog(eventTypeToEdit: EventType | null = null): void {
    this.editingEventType = eventTypeToEdit;
    this.eventTypeDialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: eventTypeToEdit ? 'Edit Event Type' : 'Add New Event Type',
        confirmText: eventTypeToEdit ? 'Update' : 'Save',
        cancelText: 'Cancel',
        template: this.addEventTypeContent,
      },
      width: '50.5rem',
      maxWidth: '100vw',
      disableClose: true,
    });

    this.eventTypeDialogRef.afterClosed().subscribe(() => {
      this.editingEventType = null;
      this.eventTypeDialogRef = null;
    });
  }

  openAddManagerDialog(managerEdit: Manager | null = null): void {
    this.editingManager = managerEdit;
    this.eventTypeDialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: managerEdit ? 'Edit Event Manager' : 'Add New Event Manager',
        confirmText: managerEdit ? 'Update' : 'Save',
        cancelText: 'Cancel',
        template: this.addEventManagerContent,
      },
      width: '40.5rem',
      maxWidth: '100vw',
      disableClose: true,
    });

    this.eventTypeDialogRef.afterClosed().subscribe(() => {
      this.editingManager = null;
      this.eventTypeDialogRef = null;
    });
  }

  openAddAudienceDialog(audienceType: Manager | null = null): void {
    this.editingManager = audienceType;
    this.eventTypeDialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: audienceType ? 'Edit Audience Type' : 'Add New Audience Type',
        confirmText: audienceType ? 'Update' : 'Save',
        cancelText: 'Cancel',
        template: this.addEventAudienceTypeContent,
      },
      width: '40.5rem',
      maxWidth: '100vw',
      disableClose: true,
    });

    this.eventTypeDialogRef.afterClosed().subscribe(() => {
      this.editingManager = null;
      this.eventTypeDialogRef = null;
    });
  }

  openAddFocusAreaDialog(focusArea: Manager | null = null): void {
    this.editingManager = focusArea;
    this.eventTypeDialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: focusArea ? 'Edit Focus Area' : 'Add New Focus Area',
        confirmText: focusArea ? 'Update' : 'Save',
        cancelText: 'Cancel',
        template: this.addEventFocusAreaContent,
      },
      width: '40.5rem',
      maxWidth: '100vw',
      disableClose: true,
    });

    this.eventTypeDialogRef.afterClosed().subscribe(() => {
      this.editingManager = null;
      this.eventTypeDialogRef = null;
    });
  }

  openAddRegionDialog(region: Manager | null = null): void {
    this.editingManager = region;
    this.eventTypeDialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: region ? 'Edit Region' : 'Add New Region',
        confirmText: region ? 'Update' : 'Save',
        cancelText: 'Cancel',
        template: this.addEventRegionContent,
      },
      width: '40.5rem',
      maxWidth: '100vw',
      disableClose: true,
    });

    this.eventTypeDialogRef.afterClosed().subscribe(() => {
      this.editingManager = null;
      this.eventTypeDialogRef = null;
    });
  }

  onEventTypeFormSubmit(formData: any): void {
    const payload = {
      eventTypeName: formData.eventTypeName,
      receivedAttendingPoints: formData.pointsAwarded,
    };

    const apiCall = this.editingEventType
      ? this.httpService.put(`EventTypes/${this.editingEventType.id}`, payload)
      : this.httpService.post('EventTypes', payload);

    this.loading = true;
    apiCall.subscribe({
      next: (response: any) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          this.eventTypeDialogRef?.close();
          if (response.data) {
            const eventType = {
              label: response.data.name,
              value: response.data.eventTypeId,
            };
            this.eventOptions.push(eventType);
            this.eventForm.patchValue({ eventType: eventType.value });
            this.eventForm.get('eventType')?.markAsTouched();
            this.cdr.detectChanges();
          }
          this.editingEventType = null;
        } else {
          this.showErrorToast(response.message);
        }
      },
      error: (err) => {
        this.showErrorToast(err?.message);
      },
      complete: () => (this.loading = false),
    });
  }

  onEventManagerFormSubmit(formData: any): void {
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.primaryEmail,
      secondaryEmail: formData.secondaryEmail,
      mobileNumber: formData.mobileNumber,
    };

    const apiCall = this.editingManager
      ? this.httpService.put(`eventmanagers/${this.editingManager.id}`, payload)
      : this.httpService.post('eventmanagers', payload);

    this.loading = true;
    apiCall.subscribe({
      next: (response: any) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          this.eventTypeDialogRef?.close();
          if (response.data) {
            const newManager = {
              label: response.data.name,
              value: response.data.managerId,
            };
            this.eventManagers.push(newManager);
            this.eventForm.patchValue({ eventManager: newManager.value });
            this.eventForm.get('eventManager')?.markAsTouched();
            this.cdr.detectChanges();
          }
          this.editingManager = null;
        } else {
          this.showErrorToast(response.message);
        }
      },
      error: (err) => this.showErrorToast(err?.message),
      complete: () => (this.loading = false),
    });
  }

  onAudienceTypeFormSubmit(formData: any): void {
    const payload = { audienceTypeName: formData.audienceTypeName };
    const apiCall = this.editingEventType
      ? this.httpService.put(
          `audiencetypes/${this.editingEventType.id}`,
          payload,
        )
      : this.httpService.post('audiencetypes', payload);

    this.loading = true;
    apiCall.subscribe({
      next: (response: any) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          this.eventTypeDialogRef?.close();
          if (response.data) {
            const audienceType = {
              label: response.data.audienceTypeName,
              value: response.data.audienceTypeId,
            };
            this.audienceTypes.push(audienceType);
            this.eventForm.patchValue({ audienceTypes: audienceType.value });
            this.eventForm.get('audienceTypes')?.markAsTouched();
            this.cdr.detectChanges();
          }
          this.editingEventType = null;
        } else {
          this.showErrorToast(response.message);
        }
      },
      error: (err) => this.showErrorToast(err?.message),
      complete: () => (this.loading = false),
    });
  }

  onFocusAreaFormSubmit(formData: any): void {
    const payload = { focusAreaName: formData.focusAreaName };
    const apiCall = this.editingEventType
      ? this.httpService.put(`focusareas/${this.editingEventType.id}`, payload)
      : this.httpService.post('focusareas', payload);

    this.loading = true;
    apiCall.subscribe({
      next: (response: any) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          this.eventTypeDialogRef?.close();
          if (response.data) {
            const focusArea = {
              label: response.data.focusAreaName,
              value: response.data.focusAreaName,
            };
            this.focusAreaOptions.push(focusArea);
            this.eventForm.patchValue({ focusArea: focusArea.value });
            this.eventForm.get('focusArea')?.markAsTouched();
            this.cdr.detectChanges();
          }
          this.editingEventType = null;
        } else {
          this.showErrorToast(response.message);
        }
      },
      error: (err) => this.showErrorToast(err?.message),
      complete: () => (this.loading = false),
    });
  }

  onRegionFormSubmit(formData: any): void {
    const payload = { eventHostRegionName: formData.eventHostRegionName };
    const apiCall = this.editingEventType
      ? this.httpService.put(
          `eventhostregions/${this.editingEventType.id}`,
          payload,
        )
      : this.httpService.post('eventhostregions', payload);

    this.loading = true;
    apiCall.subscribe({
      next: (response: any) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          this.eventTypeDialogRef?.close();
          if (response.data) {
            const region = {
              label: response.data.eventHostRegionName,
              value: response.data.eventHostRegionId,
            };
            this.regionOptions.push(region);
            this.eventForm.patchValue({ region: region.value });
            this.eventForm.get('region')?.markAsTouched();
            this.cdr.detectChanges();
          }
          this.editingEventType = null;
        } else {
          this.showErrorToast(response.message);
        }
      },
      error: (err) => this.showErrorToast(err?.message),
      complete: () => (this.loading = false),
    });
  }

  onFileSelected(event: Event, fileInputRef: HTMLInputElement): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files);

      newFiles.forEach((file) => {
        const fileType = file.type.toLowerCase();
        if (
          fileType === 'image/jpeg' ||
          fileType === 'image/jpg' ||
          fileType === 'image/png'
        ) {
          this.uploadedFiles.push(file);

          const reader = new FileReader();
          reader.onload = () => {
            this.imagePreviews.push(reader.result as string);
          };
          reader.readAsDataURL(file);
        } else {
          this.snackBar.open('Only JPG and PNG images are allowed.', 'Close', {
            duration: 3000,
          });
        }
      });

      if (this.uploadedFiles.length > 0 || this.imagePreviews.length > 0) {
        this.fileControl.setValue('uploaded');
      }
      fileInputRef.value = '';
    } else {
      this.fileControl.setValue('');
      this.fileControl.markAsTouched();
    }
  }

  removeImage(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.imagePreviews.splice(index, 1);

    if (this.uploadedFiles.length === 0 && this.imagePreviews.length === 0) {
      this.fileControl.setValue('');
      this.fileControl.markAsTouched();
    }
  }

  onFocusAreaChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const current = this.eventForm.get('focusArea')?.value || [];
    if (checkbox.checked) {
      this.eventForm.get('focusArea')?.setValue([...current, checkbox.value]);
    } else {
      this.eventForm
        .get('focusArea')
        ?.setValue(current.filter((v: string) => v !== checkbox.value));
    }
    this.eventForm.get('focusArea')?.markAsTouched();
  }

  openFilterModal(): void {
    this.showFilter = true;
    this.fetchEventsDropDown().subscribe();
    this.fetchRegions();
  }

  onStartDateChange(dateString: string): void {
    if (!this.eventAnalysisFilterForm) {
      return;
    }

    if (dateString) {
      const endControl = this.eventAnalysisFilterForm.get('endDate');
      const endDate = this.parseFilterDate(endControl?.value);
      const startDate = this.parseFilterDate(dateString);

      if (startDate && endDate && endDate <= startDate) {
        endControl?.markAsTouched();
      }
    }

    this.updateEventAnalysisDateValidation();
    this.cdr.detectChanges();
  }

  onEndDateChange(dateString: string): void {
    if (!this.eventAnalysisFilterForm) {
      return;
    }

    this.updateEventAnalysisDateValidation();
    this.cdr.detectChanges();
  }

  getMinEndDate(): string | null {
    const startDateValue =
      this.eventAnalysisFilterForm?.get('startDate')?.value ??
      this.formatDateToYMD(this.startDate);

    const date = this.parseFilterDate(startDateValue);
    if (!date) return null;

    date.setDate(date.getDate() + 0);

    return date.toISOString().split('T')[0];
  }

  closeFilterModal(): void {
    this.showFilter = false;
    document.body.classList.remove('no-scroll');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.scrollPosition);
  }

  onClearFilters(): void {
    this.selectedEventType = ['All'];
    this.selectedRegion = ['All'];
    this.refreshAllCharts();
    this.closeFilterModal();
  }

  onApplyFilters(): void {
    this.refreshAllCharts();
    this.closeFilterModal();
  }

  applyEventAnalysisFilters(): void {
    if (!this.syncAppliedFiltersFromForm()) {
      return;
    }

    this.refreshAllCharts();
  }

  resetEventAnalysisFilters(): void {
    this.selectedEventType = ['All'];
    this.selectedRegion = ['All'];
    this.setDefaultDateRange();
    this.syncEventAnalysisFormWithAppliedFilters();
    this.eventAnalysisFilterForm.markAsPristine();
    this.eventAnalysisFilterForm.markAsUntouched();
    this.refreshAllCharts();
  }

  onCustomDateChange(date: Date): void {
    this.selectedDate = date;
    this.displayDateRange = this.formatRange(
      new Date(date.getFullYear(), date.getMonth(), 1),
      new Date(date.getFullYear(), date.getMonth() + 1, 0),
    );
    this.selectedEvent = null;
  }

  onEventDateClick(date: Date): void {
    const matchedEvents = this.data.filter((evt) => {
      const [day, month, year] = evt.date.split('/').map(Number);
      const evtDate = new Date(year, month - 1, day);
      return (
        evtDate.getDate() === date.getDate() &&
        evtDate.getMonth() === date.getMonth() &&
        evtDate.getFullYear() === date.getFullYear()
      );
    });

    if (matchedEvents.length > 0) {
      this.selectedEvent = matchedEvents;
      matchedEvents.forEach((event) =>
        this.startImageRotation(event.id, event.imagePath),
      );
    } else {
      this.selectedEvent = null;
    }
  }

  startImageRotation(eventId: string, imagePath: string): void {
    if (!imagePath) return;

    try {
      const imageArray = JSON.parse(imagePath);
      this.rotatingImageMap[eventId] = imageArray;
      this.currentImageIndexMap[eventId] = 0;

      if (this.imageIntervals[eventId]) {
        clearInterval(this.imageIntervals[eventId]);
      }

      this.imageIntervals[eventId] = setInterval(() => {
        const currentIndex = this.currentImageIndexMap[eventId];
        this.currentImageIndexMap[eventId] =
          (currentIndex + 1) % imageArray.length;
      }, 3000);
    } catch (err) {
      console.error('Failed to parse imagePath:', err);
    }
  }

  onDateChange(event: any): void {
    const fromDate = event.value;
    const endOfMonth = new Date(
      fromDate.getFullYear(),
      fromDate.getMonth() + 1,
      0,
    );
    this.selectedDate = fromDate;
    this.displayDateRange = this.formatRange(fromDate, endOfMonth);
    this.selectedEvent = null;
  }

  dateClass = (date: Date) => {
    const start = this.startDate;
    const end = this.endDate;

    if (!start || !end) return '';

    if (date >= start && date <= end) {
      return 'custom-in-range';
    }

    return '';
  };

  onDateRangeChange(name: string, event: any): void {
    if (name == 'start') {
      this.startDate = event.value;
    } else {
      this.endDate = event.value;
      this.loadEventAnalysis();
      this.loadDashboardReports();
    }
  }

  setDefaultDateRange(): void {
    const today = new Date();
    this.endDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    this.startDate = new Date(today.getFullYear(), 0, 1);

    this.selectedDate = this.startDate;
  }

  getDefaultDateRange(): string {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return this.formatRange(firstDay, today);
  }

  formatRange(start: Date, end: Date): string {
    const startDay = start.getDate().toString().padStart(2, '0');
    const endDay = end.getDate().toString().padStart(2, '0');
    const monthName = start.toLocaleString('default', { month: 'long' });
    const year = start.getFullYear();
    return `${startDay} - ${endDay} ${monthName}, ${year}`;
  }

  navigateToRoute(type: string): void {
    const routes: { [key: string]: string } = {
      users: '/users',
      events: '/events/event-list',
      attendance: '/events/event-attendance',
      schools: '/schools',
      teachers: '/teachers',
      student: '/students',
      partner: '/events/event-mcc-partners',
    };

    if (routes[type] && this.roleValue != 'csquser') {
      this.router.navigate([routes[type]]);
    }
  }

  navigatetoEvent(event: any): void {
    this.router.navigate([`/events/event-list/view/${event.id}`], {
      queryParams: { eventId: event.id },
    });
  }

  onView(row: any): void {
    this.router.navigate(['/events/event-list/view/' + row.id]);
  }

  onRecentView(row: any): void {
    const routes: { [key: string]: string } = {
      School: `schools/view/${row.id}`,
      Student: `students/view/${row.id}`,
      Teacher: `teachers/view/${row.id}`,
    };

    if (routes[row.recordType]) {
      this.router.navigate([routes[row.recordType]]);
    }
  }

  switchSegment(segment: 'upcoming' | 'recent'): void {
    this.fetchRecentDetails();
    this.selectedSegment = segment;
  }

  onCreateEvent(): void {
    this.openAddDialog();
  }

  openAddDialog(
    schoolToEdit: any = null,
    isEdit: boolean = false,
    width: string = '54.5rem',
  ): void {
    const dialogRef = this.dialog.open(AddEventComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: schoolToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchEvents();
      }
    });
  }

  onAddStudent(): void {
    this.openAddStudentDialog();
  }

  openAddCSQUserDialog(
    type: string = 'user',
    csqMember: CSQTeamMember | null = null,
  ): void {
    if (this.isProcessing) return;
    this.isProcessing = true;

    if (csqMember) {
      this.csqForm.patchValue({
        firstName: csqMember.firstName || '',
        lastName: csqMember.lastName || '',
        email: csqMember.email || '',
        mobileNumber: csqMember.mobileNumber || '',
      });
    } else {
      this.csqForm.reset();
    }

    const dialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        // title: csqMember ? 'Edit CSQ User' : 'Add New CSQ User',
        title: csqMember
          ? `Edit CSQ ${type == 'admin' ? 'admin' : 'user'}`
          : `Add new CSQ ${type == 'admin' ? 'admin' : 'user'}`,
        confirmText: csqMember ? 'Update' : 'Submit',
        cancelText: 'Cancel',
        template: this.addCSQContent,
      },
      width: '50.5rem',
      maxWidth: '100vw',
      disableClose: true,
      autoFocus: false,
    });

    dialogRef.componentInstance.confirmed.subscribe((confirmed: boolean) => {
      if (confirmed && this.csqForm.valid) {
        this.saveCSQMember(csqMember?.memberId, dialogRef);
      } else if (confirmed) {
        this.markFormGroupTouched(this.csqForm);
        this.scrollToFirstInvalidControl();
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isProcessing = false;
    });
  }

  private saveCSQMember(
    memberId?: string,
    dialogRef?: MatDialogRef<any>,
  ): void {
    if (this.csqLoading) return;
    this.csqLoading = true;
    const payload = {
      ...this.csqForm.value,
      isCSQAdmin: true,
    };

    const apiCall = memberId
      ? this.httpService.put(`csqusers/${memberId}`, payload)
      : this.httpService.post('csqusers', payload);

    apiCall.subscribe({
      next: (response: any) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          // this.fetchUsers();
          dialogRef?.close();
        } else {
          this.showErrorToast(
            response.message || 'Something went wrong. Please try again later.',
          );
        }
        this.csqLoading = false;
      },
      error: (err) => {
        this.showErrorToast(err?.message);
        this.csqLoading = false;
      },
      complete: () => {
        this.csqLoading = false;
      },
    });
  }

  openAddSchoolDialog(
    schoolToEdit: any = null,
    isEdit: boolean = false,
    width: string = '50.5rem',
  ): void {
    this.editingSchool = schoolToEdit || null;
    const dialogRef = this.dialog.open(AddSchoolComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: schoolToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // this.fetchSchools();
      }
      this.editingSchool = null;
    });
  }

  selectEventType(eventType: string): void {
    // console.log('Selected event type:', eventType);
  }

  private parseEventTimeString(eventTime: string): any {
    if (!eventTime) return '';
    const rangeRegex =
      /^(\d{1,2}:\d{2}\s*[AP]M)\s+to\s+(\d{1,2}:\d{2}\s*[AP]M)$/i;
    const singleRegex = /^(\d{1,2}:\d{2}\s*[AP]M)$/i;

    const rangeMatch = eventTime.match(rangeRegex);
    if (rangeMatch) {
      return {
        from: this.to24Hour(rangeMatch[1].trim()),
        to: this.to24Hour(rangeMatch[2].trim()),
      };
    }

    const singleMatch = eventTime.match(singleRegex);
    if (singleMatch) {
      return this.to24Hour(singleMatch[1].trim());
    }

    return '';
  }

  private to24Hour(time12h: string): string {
    let [time, modifier] = time12h.split(/[\s]+/);
    let [hours, minutes] = time.split(':').map(Number);

    if (modifier?.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    }
    if (modifier?.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  private formatEventTime(eventTime: any): string {
    if (!eventTime) return '';

    if (typeof eventTime === 'object' && eventTime.from && eventTime.to) {
      return `${this.formatTime(eventTime.from)} to ${this.formatTime(eventTime.to)}`;
    }

    if (typeof eventTime === 'string' && eventTime.length > 0) {
      return this.formatTime(eventTime);
    }

    return '';
  }

  private formatTime(time: string): string {
    if (!time) return '';
    const [hourStr, minuteStr] = time.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      control.updateValueAndValidity();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private scrollToFirstInvalidControl(): void {
    setTimeout(() => {
      const firstInvalidControl = document.querySelector(
        '.ng-invalid:not(form)',
      );
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        (firstInvalidControl as HTMLElement).focus();
      }
    }, 100);
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.showStatusDropdown = !this.showStatusDropdown;

    if (this.showStatusDropdown) {
      this.addStatusDropdownClickListener();
    } else {
      this.removeStatusDropdownClickListener();
    }
    this.cdr.detectChanges();
  }

  closeStatusDropdown(): void {
    this.showStatusDropdown = false;
    this.removeStatusDropdownClickListener();
    this.cdr.detectChanges();
  }

  private addStatusDropdownClickListener(): void {
    this.removeStatusDropdownClickListener();
    this.statusDropdownClickListener = () => {
      this.closeStatusDropdown();
    };
    setTimeout(() => {
      document.addEventListener('click', this.statusDropdownClickListener!);
    }, 0);
  }

  private removeStatusDropdownClickListener(): void {
    if (this.statusDropdownClickListener) {
      document.removeEventListener('click', this.statusDropdownClickListener);
      this.statusDropdownClickListener = null;
    }
  }

  currentYear = new Date().getFullYear();

  intToDay(count: number): string {
    return `${count} day${count === 1 ? '' : 's'}`;
  }
}
